import { useContext, useEffect, useMemo, useState } from "react";
import { Button, Table, Tags } from "../../../ui";
import { Context } from "../../../../context/Context";
import { useAccess } from "../../../../access/hooks/useAccess";
import { formatSar } from "../../../../utils/billingMoney";
import {
  archiveBillingPlanAPI,
  createBillingPlanAPI,
  listBillingPlansAPI,
  updateBillingPlanAPI,
} from "../../../../utils/api/billing";
import PlanForm from "../modals/PlanForm";
import { labelFor, useBillingAdminText } from "../billingAdminI18n";

const statusVariant = { draft: "warning", active: "success", archived: "neutral" };

// L / U / D packages and integration pricing, reusing the existing
// BillingPlan CRUD. Editing a plan updates that same document in place
// (preserving its _id, code, and version) — issued invoices are unaffected
// because they already snapshot pricingSnapshot at issue time and never
// re-read the live plan.
const PackagesPanel = ({ canManage, onChanged }) => {
  const { lang } = useContext(Context);
  const { can } = useAccess();
  const text = useBillingAdminText(lang);
  const token = localStorage.getItem("auth-token");
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const result = await listBillingPlansAPI(token, { limit: 100 });
      setPlans(result.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grouped = useMemo(() => {
    return plans.reduce((acc, plan) => {
      acc[plan.code] = acc[plan.code] || [];
      acc[plan.code].push(plan);
      return acc;
    }, {});
  }, [plans]);

  const createPlan = async (payload) => {
    if (!can("billing.admin.settings.manage")) return;
    await createBillingPlanAPI(token, payload);
    setForm(null);
    await load();
    onChanged?.();
  };

  const updatePlan = async (plan, payload) => {
    if (!can("billing.admin.settings.manage")) return;
    await updateBillingPlanAPI(token, plan._id, payload);
    setForm(null);
    await load();
    onChanged?.();
  };

  const archive = async (plan) => {
    if (!can("billing.admin.settings.manage")) return;
    await archiveBillingPlanAPI(token, plan._id);
    await load();
    onChanged?.();
  };

  const columns = [
    { key: "code", label: text.plans.code, render: (plan) => <div className="billing-plan-code"><strong>{plan.code}</strong><span>v{plan.version}</span></div> },
    { key: "names", label: text.plans.plan, render: (plan) => <div className="billing-plan-name"><strong>{plan.nameAr}</strong><span>{plan.nameEn}</span></div> },
    { key: "type", label: text.subscriptions.type, render: (plan) => labelFor(text.types, plan.type) },
    { key: "basePriceMinor", label: text.plans.basePrice, render: (plan) => formatSar(plan.basePriceMinor, lang) },
    { key: "extraBranchPriceMinor", label: text.plans.extraBranch, render: (plan) => formatSar(plan.extraBranchPriceMinor, lang) },
    { key: "highlighted", label: text.plans.highlighted, render: (plan) => <Tags variant={plan.highlighted ? "success" : "neutral"}>{plan.highlighted ? text.common.yes : text.common.no}</Tags> },
    { key: "status", label: text.common.status, render: (plan) => <Tags variant={statusVariant[plan.status] || "neutral"}>{labelFor(text.statuses, plan.status)}</Tags> },
    {
      key: "actions",
      label: text.common.actions,
      render: (plan) =>
        canManage ? (
          <div className="billing-plan-actions">
            <Button size="sm" appearance="outline" onClick={() => setForm({ mode: "edit", plan })}>
              {text.plans.update}
            </Button>
            {plan.status !== "archived" && (
              <Button size="sm" variant="danger" appearance="soft" onClick={() => archive(plan)}>
                {text.plans.archive}
              </Button>
            )}
          </div>
        ) : null,
    },
  ];

  return (
    <div className="billing-settings-panel">
      <p className="billing-settings-panel-note">{text.settings.packageModal.description}</p>
      {error && <div className="billing-error">{error}</div>}
      {form ? (
        <PlanForm
          includeCode={form.mode === "create"}
          initialValues={
            form.plan
              ? {
                  nameAr: form.plan.nameAr,
                  nameEn: form.plan.nameEn,
                  descriptionAr: form.plan.descriptionAr || "",
                  descriptionEn: form.plan.descriptionEn || "",
                  type: form.plan.type,
                  highlighted: Boolean(form.plan.highlighted),
                  billingCycle: form.plan.billingCycle,
                  basePrice: (form.plan.basePriceMinor / 100).toFixed(2),
                  extraBranchPrice: (form.plan.extraBranchPriceMinor / 100).toFixed(2),
                  includedBranchCount: form.plan.includedBranchCount,
                  featuresText: (form.plan.features || []).join("\n"),
                  effectiveFrom: form.plan.effectiveFrom ? String(form.plan.effectiveFrom).slice(0, 10) : "",
                  status: form.plan.status,
                }
              : {}
          }
          submitLabel={form.mode === "create" ? text.plans.create : text.plans.update}
          onSubmit={(payload) => (form.mode === "create" ? createPlan(payload) : updatePlan(form.plan, payload))}
          onCancel={() => setForm(null)}
        />
      ) : (
        <>
          <Table columns={columns} data={plans} loading={loading} emptyText={text.plans.empty} />
          <div className="billing-version-groups">
            {Object.entries(grouped).map(([code, versions]) => (
              <div key={code}>
                <strong>{code}</strong>
                <span>{versions.map((plan) => `v${plan.version} ${labelFor(text.statuses, plan.status)}`).join(" | ")}</span>
              </div>
            ))}
          </div>
          {canManage && (
            <Button appearance="filled" onClick={() => setForm({ mode: "create" })}>
              {text.plans.create}
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default PackagesPanel;
